import { useCallback, useEffect, useState } from "react";

import { loadMaintenanceConfig } from "@/lib/maintenance/remoteConfig";
import {
  getMaintenancePhase,
  getNextBoundary,
  MaintenanceApp,
  MAINTENANCE_BYPASS_STORAGE_KEY,
  MaintenanceConfig,
  matchesMaintenanceBypassCode,
} from "@/lib/maintenance/schedule";

// Raw string (not JSON) so the stored value matches the other Tournated apps.
const DISMISSED_STORAGE_KEY = "tournated:maintenance-dismissed";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
// setTimeout overflows past ~24.8 days; the poll re-arms it long before then.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
const APP: MaintenanceApp = "admin";

const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the value lasts for this page only.
  }
};

const readDismissedKey = () => readStored(DISMISSED_STORAGE_KEY);
const readBypassCode = () => readStored(MAINTENANCE_BYPASS_STORAGE_KEY);

export const useMaintenanceState = () => {
  const [config, setConfig] = useState<MaintenanceConfig | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [dismissedKey, setDismissedKey] = useState<string | null>(readDismissedKey);
  const [bypassCode, setBypassCode] = useState<string | null>(readBypassCode);

  useEffect(() => {
    let isMounted = true;

    const refresh = async () => {
      const next = await loadMaintenanceConfig();
      if (!isMounted) return;
      if (next) setConfig(next);
      setNow(new Date());
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };

    void refresh();
    const interval = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const schedule = config?.schedule ?? null;

  // Re-render exactly when the notice opens, the window starts or it ends.
  useEffect(() => {
    if (!schedule) return;
    const boundary = getNextBoundary(schedule, now);
    if (!boundary) return;
    const delay = Math.min(boundary.getTime() - now.getTime() + 1000, MAX_TIMEOUT_MS);
    const timeout = window.setTimeout(() => setNow(new Date()), delay);
    return () => window.clearTimeout(timeout);
  }, [schedule, now]);

  const phase = schedule ? getMaintenancePhase(schedule, APP, now) : "none";
  // Keyed by phase so a notice dismissed while upcoming shows once more when the window starts.
  const noticeKey = schedule ? `${schedule.id}:${phase}` : null;
  const isDismissed = !!schedule?.dismissible && dismissedKey === noticeKey;

  const dismiss = useCallback(() => {
    if (!noticeKey) return;
    setDismissedKey(noticeKey);
    writeStored(DISMISSED_STORAGE_KEY, noticeKey);
  }, [noticeKey]);

  // A code saved on this device only counts while it is still the configured one.
  const isBypassed = !!config && matchesMaintenanceBypassCode(config, bypassCode);
  const canBypass = !!config?.bypassCode;

  const bypass = useCallback(
    (code: string) => {
      if (!config || !matchesMaintenanceBypassCode(config, code)) return false;
      setBypassCode(config.bypassCode);
      writeStored(MAINTENANCE_BYPASS_STORAGE_KEY, config.bypassCode);
      return true;
    },
    [config]
  );

  return { config, schedule, phase, isDismissed, dismiss, isBypassed, canBypass, bypass };
};
